/**
 * Route Hazard Service
 * 
 * Allows field workers to report and view hazards/notes on routes.
 * Hazards are shared with team in real-time via Firestore.
 * 
 * Features:
 * - Road hazard reporting (pothole, washout, flooding)
 * - Gate/access notes (locked gate, code changed)
 * - Wildlife/livestock crossings
 * - Custom notes for any location
 * - Auto-expiration after 24 hours
 * - Team-wide visibility
 */

import { db } from '../firebase/client';
import {
    collection,
    addDoc,
    query,
    where,
    getDocs,
    deleteDoc,
    doc,
    onSnapshot,
    Timestamp,
    GeoPoint
} from 'firebase/firestore';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type HazardType =
    | 'road_hazard'      // Pothole, rough road, washout
    | 'road_closed'      // Completely impassable
    | 'gate_locked'      // Gate is locked, need to find alternate
    | 'flooding'         // Water on road
    | 'wildlife'         // Animals on/near road
    | 'construction'     // Road work ahead
    | 'note';            // General note

export interface RouteHazard {
    id: string;
    type: HazardType;
    location: {
        lat: number;
        lng: number;
    };
    description: string;
    severity: 'low' | 'medium' | 'high';
    reportedBy: {
        userId: string;
        userName: string;
    };
    reportedAt: Date;
    expiresAt: Date;
    verified: boolean;
    verifiedBy?: string;
    companyId: string;

    // Optional: associated well
    nearWellId?: string;
    nearWellName?: string;
}

export interface CreateHazardInput {
    type: HazardType;
    location: { lat: number; lng: number };
    description: string;
    severity?: 'low' | 'medium' | 'high';
    nearWellId?: string;
    nearWellName?: string;
}

// ============================================================================
// HAZARD ICONS & COLORS
// ============================================================================

export const HAZARD_CONFIG: Record<HazardType, {
    icon: string;
    color: string;
    label: string;
    defaultExpiry: number; // hours
}> = {
    road_hazard: {
        icon: '⚠️',
        color: 'orange',
        label: 'Road Hazard',
        defaultExpiry: 48
    },
    road_closed: {
        icon: '🚫',
        color: 'red',
        label: 'Road Closed',
        defaultExpiry: 72
    },
    gate_locked: {
        icon: '🔒',
        color: 'yellow',
        label: 'Gate Locked',
        defaultExpiry: 24
    },
    flooding: {
        icon: '🌊',
        color: 'blue',
        label: 'Flooding',
        defaultExpiry: 24
    },
    wildlife: {
        icon: '🦌',
        color: 'green',
        label: 'Wildlife',
        defaultExpiry: 4
    },
    construction: {
        icon: '🚧',
        color: 'orange',
        label: 'Construction',
        defaultExpiry: 168 // 1 week
    },
    note: {
        icon: '📝',
        color: 'gray',
        label: 'Note',
        defaultExpiry: 24
    },
};

// ============================================================================
// SERVICE CLASS
// ============================================================================

class RouteHazardService {
    private currentCompanyId: string | null = null;
    private unsubscribe: (() => void) | null = null;
    private hazardListeners: Set<(hazards: RouteHazard[]) => void> = new Set();
    private cachedHazards: RouteHazard[] = [];

    /**
     * Initialize the service with tenant context
     */
    initialize(companyId: string): void {
        if (this.currentCompanyId === companyId) return;

        this.currentCompanyId = companyId;
        this.startListening();
    }

    /**
     * Start real-time listening for hazards
     */
    private startListening(): void {
        if (!this.currentCompanyId) return;

        // Clean up existing listener
        if (this.unsubscribe) {
            this.unsubscribe();
        }

        const hazardsRef = collection(db, 'companies', this.currentCompanyId, 'route_hazards');
        const now = Timestamp.now();

        // Query only non-expired hazards
        const q = query(
            hazardsRef,
            where('expiresAt', '>', now)
        );

        this.unsubscribe = onSnapshot(q, (snapshot) => {
            this.cachedHazards = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                reportedAt: doc.data().reportedAt?.toDate() || new Date(),
                expiresAt: doc.data().expiresAt?.toDate() || new Date(),
                location: {
                    lat: doc.data().location?.latitude || doc.data().location?.lat,
                    lng: doc.data().location?.longitude || doc.data().location?.lng,
                }
            })) as RouteHazard[];

            // Notify all listeners
            this.hazardListeners.forEach(listener => listener(this.cachedHazards));
        }, (error) => {
            console.error('Error listening to hazards:', error);
        });
    }

    /**
     * Subscribe to hazard updates
     */
    subscribe(callback: (hazards: RouteHazard[]) => void): () => void {
        this.hazardListeners.add(callback);

        // Immediately send cached hazards
        if (this.cachedHazards.length > 0) {
            callback(this.cachedHazards);
        }

        // Return unsubscribe function
        return () => {
            this.hazardListeners.delete(callback);
        };
    }

    /**
     * Report a new hazard
     */
    async reportHazard(
        input: CreateHazardInput,
        user: { id: string; name: string }
    ): Promise<string> {
        if (!this.currentCompanyId) {
            throw new Error('Hazard service not initialized');
        }

        const config = HAZARD_CONFIG[input.type];
        const expiryHours = config?.defaultExpiry || 24;
        const expiresAt = new Date(Date.now() + expiryHours * 60 * 60 * 1000);

        const hazardData = {
            type: input.type,
            location: new GeoPoint(input.location.lat, input.location.lng),
            description: input.description,
            severity: input.severity || 'medium',
            reportedBy: {
                userId: user.id,
                userName: user.name,
            },
            reportedAt: Timestamp.now(),
            expiresAt: Timestamp.fromDate(expiresAt),
            verified: false,
            companyId: this.currentCompanyId,
            nearWellId: input.nearWellId,
            nearWellName: input.nearWellName,
        };

        const hazardsRef = collection(db, 'companies', this.currentCompanyId, 'route_hazards');
        const docRef = await addDoc(hazardsRef, hazardData);

        return docRef.id;
    }

    /**
     * Delete a hazard (reporter or admin only)
     */
    async deleteHazard(hazardId: string): Promise<void> {
        if (!this.currentCompanyId) {
            throw new Error('Hazard service not initialized');
        }

        const docRef = doc(db, 'companies', this.currentCompanyId, 'route_hazards', hazardId);
        await deleteDoc(docRef);
    }

    /**
     * Get hazards within a certain radius of a point
     */
    getHazardsNear(
        location: { lat: number; lng: number },
        radiusMiles: number = 5
    ): RouteHazard[] {
        return this.cachedHazards.filter(hazard => {
            const distance = this.calculateDistance(
                location.lat,
                location.lng,
                hazard.location.lat,
                hazard.location.lng
            );
            return distance <= radiusMiles;
        });
    }

    /**
     * Get hazards along a route (within buffer distance of any point)
     */
    getHazardsAlongRoute(
        waypoints: Array<{ lat: number; lng: number }>,
        bufferMiles: number = 0.5
    ): RouteHazard[] {
        return this.cachedHazards.filter(hazard => {
            return waypoints.some(point => {
                const distance = this.calculateDistance(
                    point.lat,
                    point.lng,
                    hazard.location.lat,
                    hazard.location.lng
                );
                return distance <= bufferMiles;
            });
        });
    }

    /**
     * Haversine distance calculation
     */
    private calculateDistance(
        lat1: number,
        lng1: number,
        lat2: number,
        lng2: number
    ): number {
        const R = 3959; // Earth's radius in miles
        const dLat = this.toRad(lat2 - lat1);
        const dLng = this.toRad(lng2 - lng1);
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
            Math.sin(dLng / 2) * Math.sin(dLng / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    }

    private toRad(deg: number): number {
        return deg * (Math.PI / 180);
    }

    /**
     * Clean up on unmount
     */
    dispose(): void {
        if (this.unsubscribe) {
            this.unsubscribe();
            this.unsubscribe = null;
        }
        this.hazardListeners.clear();
        this.cachedHazards = [];
    }
}

// Singleton export
export const routeHazardService = new RouteHazardService();
